import React, { useRef, useState } from "react";
import { ChevronLeft, MoreHorizontal } from "lucide-react";
import PullToRefresh from "./pull-to-refresh";
import ActionSheet, { ActionSheetItem } from "./action-sheet";
import FloatingActionButton from "./floating-action-button";
import MobileIconButton from "./mobile-icon-button";
import MobileStatsStrip from "./mobile-stats-strip";
import MobileInsights from "./mobile-insights";
import { MobilePrimaryAction, StatItem } from "./types";
import { cn } from "../../lib/utils";

export interface MobileDetailLayoutProps {
  title: string;
  /** Small label above the title, e.g. the entity type. */
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Rendered next to the subtitle, e.g. a status badge. */
  badge?: React.ReactNode;
  /** Back is a handler (not a route) so callers can pass navigate state. */
  onBack: () => void;
  onRefresh?: () => unknown;
  primaryAction?: MobilePrimaryAction;
  actions?: Array<ActionSheetItem | false | null | undefined>;
  stats?: StatItem[];
  insights?: React.ReactNode;
  /** Pinned above the tab bar; replaces the FAB while shown. */
  bottomBar?: React.ReactNode;
  children: React.ReactNode;
}

const COLLAPSE_AFTER = 72;

const MobileDetailLayout: React.FC<MobileDetailLayoutProps> = ({
  title,
  eyebrow,
  subtitle,
  badge,
  onBack,
  onRefresh,
  primaryAction,
  actions,
  stats,
  insights,
  bottomBar,
  children,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const lastScrollTop = useRef(0);

  const visibleActions = (actions ?? []).filter(
    (action): action is ActionSheetItem => !!action,
  );

  const handleScroll = (scrollTop: number) => {
    setIsCollapsed(scrollTop > COLLAPSE_AFTER);
    const delta = scrollTop - lastScrollTop.current;
    if (Math.abs(delta) > 6)
      setIsScrollingDown(delta > 0 && scrollTop > COLLAPSE_AFTER);
    lastScrollTop.current = scrollTop;
  };

  const backButton = (
    <MobileIconButton label="Back" onClick={onBack}>
      <ChevronLeft className="h-5 w-5" />
    </MobileIconButton>
  );

  const moreButton = visibleActions.length > 0 && (
    <MobileIconButton label="More actions" onClick={() => setActionsOpen(true)}>
      <MoreHorizontal className="h-5 w-5" />
    </MobileIconButton>
  );

  const body = (
    <div
      className={cn(
        "min-w-0 space-y-5 px-4 pt-[calc(env(safe-area-inset-top)_+_1rem)]",
        primaryAction || bottomBar ? "pb-28" : "pb-8",
      )}
    >
      <header className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          {backButton}
          {moreButton}
        </div>
        <div className="min-w-0">
          {eyebrow && (
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {eyebrow}
            </p>
          )}
          <h1 className="mt-0.5 break-words text-[28px] font-bold leading-tight font-jakarta text-gray-900">
            {title}
          </h1>
          {(subtitle || badge) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {subtitle && (
                <span className="min-w-0 text-[13px] leading-snug text-gray-500">
                  {subtitle}
                </span>
              )}
              {badge}
            </div>
          )}
        </div>
      </header>

      {stats && stats.length > 0 && <MobileStatsStrip items={stats} />}
      {insights && <MobileInsights>{insights}</MobileInsights>}

      <div className="space-y-4">{children}</div>
    </div>
  );

  return (
    <div className="relative h-full overflow-hidden bg-gray-50">
      <div
        className={cn(
          "absolute inset-x-0 top-0 z-20 border-b border-gray-100 bg-white/90 pt-[env(safe-area-inset-top)] backdrop-blur-md transition-all duration-200",
          isCollapsed
            ? "opacity-100"
            : "pointer-events-none -translate-y-2 opacity-0",
        )}
      >
        <div className="flex h-14 items-center gap-2 px-4">
          {backButton}
          <span className="min-w-0 flex-1 truncate text-base font-bold font-jakarta text-gray-900">
            {title}
          </span>
          {moreButton}
        </div>
      </div>

      {onRefresh ? (
        <PullToRefresh
          onRefresh={async () => {
            await onRefresh();
          }}
          onScroll={handleScroll}
        >
          {body}
        </PullToRefresh>
      ) : (
        <div
          className="h-full overflow-y-auto overscroll-y-contain"
          onScroll={(event) => handleScroll(event.currentTarget.scrollTop)}
        >
          {body}
        </div>
      )}

      {bottomBar ? (
        <div className="absolute inset-x-0 bottom-0 z-30">{bottomBar}</div>
      ) : (
        primaryAction && (
          <FloatingActionButton
            action={primaryAction}
            compact={isScrollingDown}
          />
        )
      )}

      {visibleActions.length > 0 && (
        <ActionSheet
          open={actionsOpen}
          onClose={() => setActionsOpen(false)}
          title={title}
          actions={visibleActions}
        />
      )}
    </div>
  );
};

export default MobileDetailLayout;
